import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { CenterModel } from './center.model';
import { CenterService } from './center.service';
import { CenterFormComponent } from './center-form.component';
import { ContactService } from '../contact/contact.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';

export interface CenterListDialogData {
  contactUid: string;
  showFaceFields: boolean;
}

@Component({
    selector: 'convfact-center-list',
    templateUrl: './center-list.component.html',
    styleUrls: ['./center.component.css'],
    standalone: false
})
export class CenterListComponent implements OnInit {
  contactUid: string;
  showFaceFields = false;
  dataSource: CenterModel[] = [];
  ready = false;
  loadError = false;

  static open(dialog: MatDialog, data: CenterListDialogData): Observable<void> {
    return dialog.open<CenterListComponent, CenterListDialogData>(CenterListComponent, {
      width: '640px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed();
  }

  constructor(
    private centerService: CenterService,
    private contactService: ContactService,
    private route: ActivatedRoute,
    private confirm: ConfirmService,
    private notify: NotificationService,
    public dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<CenterListComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: CenterListDialogData,
  ) {
    if (data) {
      this.contactUid = data.contactUid;
      this.showFaceFields = data.showFaceFields === true;
    } else {
      this.contactUid = this.route.parent?.snapshot.paramMap.get('contactUid') || this.route.snapshot.paramMap.get('contactUid');
    }
  }

  get isDialog(): boolean {
    return !!this.dialogRef;
  }

  get empty(): boolean {
    return this.dataSource.length === 0;
  }

  ngOnInit(): void {
    if (!this.isDialog && this.contactUid) {
      this.contactService.loadData(this.contactUid)
        .then(contact => { this.showFaceFields = !!contact && (contact.isJuridical === true || contact.isPublicAdmin === true); })
        .catch(() => { this.showFaceFields = false; });
    }
    this.load();
  }

  load(): void {
    if (!this.contactUid) {
      this.loadError = true;
      this.ready = true;
      return;
    }
    this.ready = false;
    this.loadError = false;
    this.centerService.getListByContact(this.contactUid)
      .then(data => { this.dataSource = Array.isArray(data) ? data : []; })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  create(): void {
    CenterFormComponent.open(this.dialog, { uid: null, contactUid: this.contactUid, showFaceFields: this.showFaceFields })
      .subscribe(changed => { if (changed) this.load(); });
  }

  edit(item: CenterModel): void {
    CenterFormComponent.open(this.dialog, { uid: item.uid, contactUid: this.contactUid, showFaceFields: this.showFaceFields })
      .subscribe(changed => { if (changed) this.load(); });
  }

  delete(item: CenterModel): void {
    this.confirm.open({
      title: 'Eliminar centro',
      message: 'Se eliminará el centro del contacto. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando centro…',
      errorText: 'No se pudo eliminar el centro',
      action: () => this.centerService.deleteData(item.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Centro eliminado');
      this.load();
    });
  }

  close(): void {
    this.dialogRef?.close();
  }

  location(item: CenterModel): string {
    return [item.postalCode, item.location, item.state].filter(value => !!value).join(' · ');
  }

  trackByUid(_: number, item: CenterModel): string {
    return item.uid;
  }

  private details(item: CenterModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [{ label: 'Centro', value: item.name || 'Sin nombre' }];
    if (item.code) details.push({ label: 'Código', value: item.code });
    if (item.address) details.push({ label: 'Dirección', value: item.address });
    return details;
  }
}
